import classNames from "classnames";

type ProTagProps = {
  className?: string;
  testId?: string;
  variant?: "rectangular" | "pill";
};

/**
 * ProTag Component
 *
 * A simple tag displaying "Pro" text, designed to replace the old blue triangle
 * implementation on Pro article cards. This component is purely presentational.
 *
 * Design: Based on Figma Design System v1.0
 * - Rectangular variant: Node 15893:1514
 * - Pill variant: Node 3499:21942 (Homepage v2.0)
 *
 * Background: accent-bigblue (#2322ff)
 * Text: White, Aktiv Grotesk Bold
 *
 * Variants:
 * - **rectangular** (default): No border radius, 14px text, 6px horizontal padding
 * - **pill**: Fully rounded (12px radius), 12px text, 8px horizontal padding
 *
 * Usage:
 * ```tsx
 * <ProTag className="absolute bottom-4 right-4" />
 * <ProTag variant="pill" />
 * ```
 */
const ProTag = ({
  className,
  testId = "pro-tag",
  variant = "rectangular",
}: ProTagProps) => {
  const isPill = variant === "pill";

  return (
    <div
      className={classNames(
        "flex items-center justify-center bg-pro-blue py-1",
        {
          "px-1.5": !isPill,
          "rounded-[12px] px-2": isPill,
          "h-[20px] w-[36px]": isPill,
        },
        className
      )}
      data-testid={testId}
    >
      <p
        className={classNames("text-white", {
          "font-emphasis-14": !isPill,
          "font-bold text-[12px] leading-[1.3]": isPill,
        })}
      >
        Pro
      </p>
    </div>
  );
};

export default ProTag;
